"use client";

import { createContext, useContext, useEffect, useMemo } from "react";
import { DEFAULT_LESSON_PREFERENCES, type LessonPreferences } from "@lib/lessonPreferences";
import { setClientLessonPreferences } from "@lib/lessonPreferencesClient";

type LessonPreferencesContextValue = {
  preferences: LessonPreferences;
  sceneVideoAvailable: boolean;
};

const LessonPreferencesContext = createContext<LessonPreferencesContextValue>({
  preferences: DEFAULT_LESSON_PREFERENCES,
  sceneVideoAvailable: false,
});

export function LessonPreferencesProvider({
  preferences,
  sceneVideoAvailable = false,
  children,
}: {
  preferences: LessonPreferences;
  sceneVideoAvailable?: boolean;
  children: React.ReactNode;
}) {
  const value = useMemo(
    (): LessonPreferencesContextValue => ({
      preferences,
      sceneVideoAvailable,
    }),
    [preferences, sceneVideoAvailable],
  );

  useEffect(() => {
    setClientLessonPreferences(value.preferences);
  }, [value.preferences]);

  return (
    <LessonPreferencesContext.Provider value={value}>{children}</LessonPreferencesContext.Provider>
  );
}

export function useLessonPreferences(): LessonPreferences {
  return useContext(LessonPreferencesContext).preferences;
}

export function useSceneVideoAvailable(): boolean {
  return useContext(LessonPreferencesContext).sceneVideoAvailable;
}
